import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import { email, required, useGetIdentity, useRecordContext } from "ra-core";
import { officialEmail } from "@/lib/officialEmail";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import {
  isLegacyBusinessArm,
  type AppRole,
  type BusinessArm,
  type Sale,
} from "../types";
import { getBusinessUnitChoices } from "./businessArms";
import { BusinessUnitReviewBadge } from "./BusinessUnitReviewBadge";
import { ROLE_CHOICES } from "./roles";
import { SalesLevelInputs } from "./SalesLevelInputs";

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

/**
 * The `reports_to` (acompañamiento) choices offered to a lead_manager are
 * limited to her own brazo scope (Almikia Epic E3b, TASK-063), mirroring
 * `isInLeadManagerScope` in canAccess.ts / the users-function: every user
 * when her own brazo is 'ambos'/'direccion_general', same-or-'ambos'-brazo
 * users otherwise, nobody when her own brazo is NULL (fail-closed -- "()"
 * is the FakeRest/postgrest empty-`@in`-list convention). Direccion (and
 * any other role, though only direccion/lead_manager ever reach this form)
 * gets no extra filter.
 */
const buildReportsToScopeFilter = (
  identityRole: AppRole | undefined,
  identityBusinessArm: BusinessArm | null | undefined,
): Record<string, unknown> | undefined => {
  if (identityRole !== "lead_manager") {
    return undefined;
  }
  if (
    identityBusinessArm === "ambos" ||
    identityBusinessArm === "direccion_general"
  ) {
    return undefined;
  }
  if (identityBusinessArm == null) {
    return { "business_arm@in": "()" };
  }
  return { "business_arm@in": `(${identityBusinessArm},ambos)` };
};

export function SalesInputs({
  onBusinessArmChange,
}: {
  /** Reports the unit currently picked in the form (the projects card lives outside it). */
  onBusinessArmChange?: (arm: BusinessArm | null | undefined) => void;
}) {
  const { identity } = useGetIdentity();
  const record = useRecordContext<Sale>();
  const watchedRole = useWatch({ name: "role" }) as AppRole | undefined;
  const watchedBusinessArm = useWatch({ name: "business_arm" }) as
    | BusinessArm
    | null
    | undefined;
  useEffect(() => {
    onBusinessArmChange?.(watchedBusinessArm);
  }, [onBusinessArmChange, watchedBusinessArm]);
  const isLeadManager = identity?.role === "lead_manager";
  const isSelf = record?.id === identity?.id;
  // A lead_manager only ever changes Brazo, Acompañamiento and Clave de
  // asesora on an in-scope user (record-level access is already enforced by
  // canAccess.ts before this form renders) -- name, email, role and disabled
  // stay read-only for her.
  const readOnlyForLeadManager = isLeadManager;
  // On her OWN record, `canSetScopedOrgField` in the users-function
  // (supabase/functions/users/orgFields.ts) never lets a non-direccion actor
  // set her own business_arm/reports_to -- a save would otherwise fail with
  // a generic 401. Mirror that lock in the UI so a lead_manager sees these
  // as read-only on herself instead of hitting an opaque error.
  const orgFieldsReadOnlyOnSelf = isLeadManager && isSelf;
  // advisor_code follows the users-function self-service rule: once set, a
  // non-direccion actor can no longer change her own code.
  const advisorCodeReadOnly = isLeadManager && isSelf && !!record?.advisor_code;

  return (
    <div className="space-y-4 w-full">
      <TextInput
        source="first_name"
        validate={required()}
        readOnly={readOnlyForLeadManager}
        helperText={false}
      />
      <TextInput
        source="last_name"
        validate={required()}
        readOnly={readOnlyForLeadManager}
        helperText={false}
      />
      <TextInput
        source="email"
        validate={
          record?.id
            ? [required(), email()]
            : [required(), email(), officialEmail()]
        }
        readOnly={readOnlyForLeadManager}
        helperText={false}
      />
      <SelectInput
        source="role"
        choices={ROLE_CHOICES}
        translateChoice={false}
        defaultValue="asesora"
        validate={required()}
        readOnly={readOnlyForLeadManager || isSelf}
        helperText={false}
      />
      <SelectInput
        source="business_arm"
        choices={getBusinessUnitChoices(
          (watchedRole ?? record?.role) as AppRole | undefined,
          record?.business_arm,
        )}
        translateChoice={false}
        label="resources.sales.fields.business_arm"
        readOnly={orgFieldsReadOnlyOnSelf}
        helperText={false}
      />
      {isLegacyBusinessArm(record?.business_arm) &&
      isLegacyBusinessArm(watchedBusinessArm) ? (
        <BusinessUnitReviewBadge />
      ) : null}
      {/*
       * The Independence guide (docs/almikia/product-rules.md) bans
       * hierarchy wording ("reporta a", "jefe") for the link between two
       * collaborators -- the label reads "Acompañamiento", never
       * "Reports to" / "Manager". Excludes the record being edited so a
       * user can never be linked to themselves.
       */}
      <ReferenceInput
        source="reports_to"
        reference="sales"
        filter={{
          ...(record?.id ? { "id@neq": record.id } : undefined),
          ...buildReportsToScopeFilter(
            identity?.role as AppRole | undefined,
            identity?.business_arm as BusinessArm | null | undefined,
          ),
        }}
        sort={{ field: "last_name", order: "ASC" }}
      >
        <AutocompleteInput
          label="resources.sales.fields.reports_to"
          helperText={false}
          clearable
          readOnly={orgFieldsReadOnlyOnSelf}
          optionText={saleOptionRenderer}
        />
      </ReferenceInput>
      <TextInput
        source="advisor_code"
        label="resources.sales.fields.advisor_code"
        readOnly={advisorCodeReadOnly}
        helperText={false}
      />
      <SalesLevelInputs />
      <BooleanInput
        source="disabled"
        readOnly={readOnlyForLeadManager || isSelf}
        helperText={false}
      />
    </div>
  );
}
